import { supabase } from '../lib/supabase';
import { getCurrentOrgId } from '../lib/getCurrentOrgId';

/**
 * Convert snake_case database fields to camelCase for React
 */
const toCamelCase = (data) => {
  if (!data) return null;
  if (Array.isArray(data)) return data?.map(toCamelCase);
  return {
    id: data?.id,
    projectId: data?.project_id,
    code: data?.code,
    name: data?.name,
    orderIndex: data?.order_index,
    createdAt: data?.created_at
  };
};

/**
 * Normalize a division code to a 2-digit zero-padded string.
 * Examples: "2" → "02", "02" → "02", "2-100" → "02", " 3 " → "03"
 * Returns null if no digits found.
 *
 * @param {string|number} code
 * @returns {string|null}
 */
export const normalizeDivisionCode = (code) => {
  const s = String(code ?? '')?.trim();
  if (!s) return null;
  const two = s?.replace(/[^0-9]/g, '')?.slice(0, 2);
  if (!two) return null;
  return two?.padStart(2, '0');
};

/**
 * Get divisions for a project.
 * Strategy: fetch both project-specific and global divisions.
 * Project-specific divisions override global ones for the same code.
 * Global divisions fill in any codes not covered by project-specific ones.
 *
 * @param {string} projectId - UUID of the project
 * @returns {Promise<Array>} Sorted array of division objects
 */
export const getByProjectId = async (projectId) => {
  try {
    // Fetch both project-specific and global divisions in one query
    const { data, error } = await supabase
      ?.from('divisions')
      ?.select('*')
      ?.or(`project_id.eq.${projectId},project_id.is.null`)
      ?.order('order_index', { ascending: true })
      ?.order('code', { ascending: true });

    if (error) throw error;

    const rows = data || [];

    // Split into project-specific and global
    const projectSpecific = rows?.filter(d => d?.project_id === projectId);
    const globalDivisions = rows?.filter(d => d?.project_id === null);

    // Normalize codes and convert to camelCase
    const normalizeRow = (row) => {
      const converted = toCamelCase(row);
      return { ...converted, code: normalizeDivisionCode(converted?.code) ?? converted?.code };
    };

    const projectMap = {};
    projectSpecific?.forEach(row => {
      const normalized = normalizeRow(row);
      const key = normalized?.code;
      if (key) projectMap[key] = normalized;
    });

    const globalMap = {};
    globalDivisions?.forEach(row => {
      const normalized = normalizeRow(row);
      const key = normalized?.code;
      if (key) globalMap[key] = normalized;
    });

    // Merge: project-specific overrides global; global fills missing codes
    const merged = { ...globalMap, ...projectMap };
    const mergedList = Object.values(merged);

    // Sort by orderIndex then code
    mergedList?.sort((a, b) => {
      const orderDiff = (a?.orderIndex || 0) - (b?.orderIndex || 0);
      if (orderDiff !== 0) return orderDiff;
      return (a?.code || '')?.localeCompare(b?.code || '');
    });

    return mergedList;
  } catch (error) {
    console.error('Error fetching divisions:', error?.message);
    return [];
  }
};

/**
 * Get all global divisions (project_id IS NULL)
 * Useful for admin/settings screens
 *
 * @returns {Promise<Array>} Sorted array of global division objects
 */
export const getGlobal = async () => {
  try {
    const { data, error } = await supabase
      ?.from('divisions')
      ?.select('*')
      ?.is('project_id', null)
      ?.order('order_index', { ascending: true })
      ?.order('code', { ascending: true });

    if (error) throw error;
    return toCamelCase(data || []);
  } catch (error) {
    console.error('Error fetching global divisions:', error?.message);
    return [];
  }
};

/**
 * Create a new division for a project.
 *
 * @param {Object} divisionData - { projectId, code, name, orderIndex }
 * @returns {Promise<Object>} Created division object
 */
export const create = async (divisionData) => {
  try {
    const orgId = await getCurrentOrgId();
    const { data, error } = await supabase
      ?.from('divisions')
      ?.insert({
        project_id: divisionData?.projectId || null,
        code: divisionData?.code,
        name: divisionData?.name,
        order_index: divisionData?.orderIndex ?? 0,
        organization_id: orgId
      })
      ?.select();

    if (error) throw error;
    return toCamelCase(data?.[0]);
  } catch (error) {
    console.error('Division INSERT failed:', error);
    throw error;
  }
};

/**
 * Update an existing division.
 *
 * @param {string} divisionId - UUID of the division to update
 * @param {Object} updates - { code, name, orderIndex }
 * @returns {Promise<Object>} Updated division object
 */
export const update = async (divisionId, updates) => {
  try {
    const payload = {};
    if (updates?.code !== undefined) payload.code = updates?.code;
    if (updates?.name !== undefined) payload.name = updates?.name;
    if (updates?.orderIndex !== undefined) payload.order_index = updates?.orderIndex;

    const { data, error } = await supabase
      ?.from('divisions')
      ?.update(payload)
      ?.eq('id', divisionId)
      ?.select()
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  } catch (error) {
    console.error('Error updating division:', error?.message);
    throw error;
  }
};

/**
 * Delete a division by ID.
 *
 * @param {string} divisionId - UUID of the division to delete
 * @returns {Promise<void>}
 */
export const remove = async (divisionId) => {
  try {
    const { error } = await supabase
      ?.from('divisions')
      ?.delete()
      ?.eq('id', divisionId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting division:', error?.message);
    throw error;
  }
};

/**
 * Extract division code from a cost code string.
 * Returns normalized 2-digit code (e.g. "02067" → "02", "2-100" → "02").
 * Returns null if cost_code is empty or has no usable digits.
 *
 * @param {string} costCode - The cost code string
 * @returns {string|null} Two-digit division code or null
 */
export const extractDivisionCode = (costCode) => {
  if (!costCode || costCode?.trim()?.length < 1) return null;
  return normalizeDivisionCode(costCode?.trim()?.substring(0, 2));
};

/**
 * Group cost code rows by their division.
 * Performs in-memory grouping — no additional DB queries.
 * Uses normalized codes for all comparisons.
 *
 * @param {Array} costCodes - Array of cost code objects (camelCase)
 * @param {Array} divisions - Array of division objects (camelCase)
 * @returns {Array} Array of { division, rows } objects sorted by division order
 */
export const groupCostCodesByDivision = (costCodes, divisions) => {
  // Build normalized divisions list — every division is guaranteed to appear
  const normalizedDivisions = (divisions || [])?.map(div => ({
    ...div,
    code: normalizeDivisionCode(div?.code) ?? div?.code
  }));

  // Build groups starting from divisions — ensures zero-cost-code divisions render
  const groups = normalizedDivisions?.map(div => ({
    division: div,
    rows: (costCodes || [])?.filter(item => {
      const divCode = extractDivisionCode(item?.costCode);
      return divCode === div?.code;
    })
  }));

  // Collect ungrouped cost codes (no matching division)
  const groupedCostCodeIds = new Set(
    groups.flatMap(g => g.rows.map(r => r?.id))
  );
  const ungroupedRows = (costCodes || [])?.filter(item => !groupedCostCodeIds?.has(item?.id));

  // Sort groups by orderIndex then code
  groups?.sort((a, b) => {
    const orderDiff = (a?.division?.orderIndex || 0) - (b?.division?.orderIndex || 0);
    if (orderDiff !== 0) return orderDiff;
    return (a?.division?.code || '')?.localeCompare(b?.division?.code || '');
  });

  // Sort rows within each group by costCode
  groups?.forEach(group => {
    group?.rows?.sort((a, b) => (a?.costCode || '')?.localeCompare(b?.costCode || ''));
  });

  // Append ungrouped rows as a special group if any exist
  if (ungroupedRows?.length > 0) {
    ungroupedRows?.sort((a, b) => (a?.costCode || '')?.localeCompare(b?.costCode || ''));
    groups?.push({
      division: { id: '__ungrouped__', code: '--', name: 'Sans division', orderIndex: 9999 },
      rows: ungroupedRows
    });
  }

  return groups;
};

const divisionsService = {
  getByProjectId,
  getGlobal,
  create,
  update,
  remove,
  extractDivisionCode,
  groupCostCodesByDivision,
  normalizeDivisionCode
};

export default divisionsService;
